import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, IconButton, Typography, Button } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, Link, useParams } from 'react-router-dom';
import DynamicBreadcrumbs from '../Shared/Breadcrumb/index.tsx';
import CommonTable from '../Shared/Tables/TableV1.tsx';
import { HomeActions } from '../../Services/_actions/home.action.js';
import SearchBox from '../FlowBuilder/Common/SearchBox/index.tsx';
import { BackButton } from '../Shared/BackButton/index.jsx';
import FormDetailsModal from '../Leads/FormDetailsModal.tsx';
import { useRecoilState } from 'recoil';
import { campaignLeadManagementAtom } from '../../Services/_states/index.js';
import LoadingDisableWrapper from '../Shared/LoadingDisableWrapper/index.tsx';

interface Supplier {
  name: string;
  area: string;
  subarea: string;
  address1: string;
  address2: string;
  supplier_id: string;
  center_area?: string;
  center_subarea?: string;
}

const EnterLeads: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const homeActions = HomeActions();
const [openFormDetailModal, setOpenFormDetailModal] = useState(false);
const [selectedRow, setSelectedRow] = useState<any>(null);
const [campaignLeadManagementData] = useRecoilState(campaignLeadManagementAtom);

  useEffect(() => {
    const fetchSuppliers = async () => {
      setLoading(true);
      try {
        if (!campaignId) {
          setSuppliers([]);
          return;
        }

        const res = await homeActions.getShortlistedSuppliers(campaignId);
        let allSuppliers: Supplier[] = [];
        const data = res?.data || {};
        const keys = Object.keys(data).filter((k) => k !== 'dynamic_suppliers');

        keys.forEach((key) => {
          const groups = data[key]?.suppliers || {};
          const centerMeta = data[key]?.center || {};

          const areaFromCenter = centerMeta.area || '-';
          const subareaFromCenter = centerMeta.subarea || '-';
          const hasNoSuppliers = Object.values(groups).every((group: any) => group.length === 0);
          if (hasNoSuppliers) {
            allSuppliers.push({
              name: '-',
              area: areaFromCenter,
              subarea: subareaFromCenter,
              address1: centerMeta.address || '-',
              address2: '-',
              supplier_id: 'N/A',
            });
          } else {
            Object.values(groups).forEach((group: any) => {
              group.forEach((supplier: any) => {
                const enrichedSupplier = {
                  ...supplier,
                  area: supplier.area || areaFromCenter,
                  subarea: supplier.subarea || subareaFromCenter,
                };
                allSuppliers.push(enrichedSupplier);
              });
            });
          }
        });

        if (Array.isArray(data.dynamic_suppliers)) {
          allSuppliers = allSuppliers.concat(data.dynamic_suppliers);
        }

        setSuppliers(allSuppliers);
      } catch (err) {
        console.error('Failed to fetch suppliers:', err);
        setSuppliers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSuppliers();
  }, [campaignId]);

  const dynamicFormFields = useMemo(() => {
  return Object.values(
    campaignLeadManagementData?.selectedForm?.leads_form_items || {}
  );
}, [campaignLeadManagementData?.selectedForm?.leads_form_items]);

const dynamicFormGroups = useMemo(() => {
  return campaignLeadManagementData?.selectedForm?.group_options || [];
}, [campaignLeadManagementData?.selectedForm?.group_options]);

const currentLeadFormId =
  campaignLeadManagementData?.selectedForm?.leads_form_id || '';

  const filteredSuppliers = useMemo(() => {
    return suppliers.filter((sup) =>
      [sup.name, sup.area, sup.subarea, sup.address1, sup.address2]
        .join(' ')
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [suppliers, search]);

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      onClick: () => navigate(''),
    },
    {
      label: 'Campaigns',
      onClick: () => navigate('/r/records/campaigns'),
    },
    { label: 'Dashboards Master', onClick: () => navigate(`/r/records/${campaignId}/form`) },
    {
      label: 'Suppliers',
    },
  ];

  const columns = useMemo(
    () => [
      {
        accessorKey: 'srNo',
        header: 'Index',
        Cell: ({ row }: any) => row.index + 1,
      },
      { accessorKey: 'name', header: 'Supplier Name' },
      { accessorKey: 'area', header: 'Area' },
      { accessorKey: 'subarea', header: 'Subarea' },
      {
        accessorKey: 'address',
        header: 'Address',
        Cell: ({ row }: any) => `${row.original.address1} ${row.original.address2}`,
      },
      {
      accessorKey: 'platformLink',
      header: 'Platform Link',
      Cell: ({ row }: any) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setSelectedRow(row.original); // optional: use in FormDetailsModal for prefill
            setOpenFormDetailModal(true);
          }}
        >
          Enter Leads
        </Button>
      ),
    },
      // {
      //   accessorKey: 'mobileLink',
      //   header: 'Mobile Link',
      //   Cell: ({ row }: any) => (
      //     <Button
      //       variant="contained"
      //       size="small"
      //       color="primary"
      //       component={Link}
      //       //  to={`/enterLeadsFromApplication/${row.original.supplier_id}`}
      //       target="_blank"
      //     >
      //       Go To Link
      //     </Button>
      //   ),
      // },
    ],
    [campaignId, navigate]
  );

  return (
    <Box>
      {/* <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(e, item) => item?.onClick?.()}
      /> */}
      <Grid container spacing={2} justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
            List of All Suppliers Of Dashboard
          </Typography>
        </Grid>
        <Grid item>
          <BackButton onClick={() => navigate(-1)} />
        </Grid>
      </Grid>
      <Box sx={{ mb: 2, width: '100%', maxWidth: 300 }}>
        <LoadingDisableWrapper loading={loading}>
        <SearchBox value={search} placeholder="Search suppliers..." handleSearch={setSearch} disabled={loading}/>
        </LoadingDisableWrapper>
      </Box>

      <CommonTable
        data={filteredSuppliers}
        columns={columns}
        state={{ isLoading: loading }}
        enablePagination={false}
      />
      {openFormDetailModal && (
  <FormDetailsModal
    open={openFormDetailModal}
    onClose={(data, type) => {
      // You can mirror LeadList's behavior:
      // - type === 'create' -> optionally refetch or show a toast
      // - type === 'update' -> not relevant when creating
      setOpenFormDetailModal(false);
      setSelectedRow(null);
    }}
    selectedRow={null} // keep null to create a new lead (same as Add Lead)
    dynamicFormFields={dynamicFormFields}
    dynamicFormGroups={dynamicFormGroups}
    currentCampaignId={campaignId || ''}
    currentLeadFormId={currentLeadFormId || ''}
    parentLead={null}
  />
)}
    </Box>
    
  );
};

export default EnterLeads;
